import { ScrollableRegion } from '@syncanix/ui';

import { useT } from '@/core/I18nProvider';

import {
  GRADED_REPO_ROWS,
  SYNTHETIC_ROWS,
  VENDORED_FIXTURE_ROWS,
  pct,
  score,
} from './benchmark-data';

import type { ReactElement, ReactNode } from 'react';

/**
 * The three result tables of the `/benchmarks` page (launch-readiness B10),
 * extracted from `pages/benchmarks/Benchmarks.tsx` to keep the page under the
 * file-size ceiling. Data comes exclusively from the test-pinned SOT in
 * `./benchmark-data`; numeric/code cells are `dir="ltr"` so RTL locales keep
 * figures readable.
 */

const TH =
  'px-3.5 pb-2.5 pt-3 text-start font-mono rtl:font-sans text-[10.5px] uppercase tracking-[0.13em] rtl:normal-case rtl:tracking-normal text-[color:var(--bd-faint)]';
const TD =
  'px-3.5 py-2.5 text-sm text-[color:var(--bd-body)] transition-colors group-hover:bg-[color:var(--bd-sunk)]';

function ResultTable({
  captionKey,
  headerKeys,
  children,
}: {
  captionKey: string;
  headerKeys: readonly string[];
  children: ReactNode;
}): ReactElement {
  const { t } = useT();
  return (
    <ScrollableRegion
      aria-label={t(captionKey)}
      className="mt-5 overflow-x-auto rounded-[var(--bd-radius)] border border-[color:var(--bd-line)]"
    >
      <table className="w-full border-collapse">
        {/* sr-only accessible name so the table rotor can name each table (I144) */}
        <caption className="sr-only">{t(captionKey)}</caption>
        <thead className="border-b-2 border-[color:var(--bd-line-3)] bg-[color:var(--bd-page)]">
          <tr>
            {headerKeys.map((key) => (
              <th key={key} scope="col" className={TH}>
                {t(key)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </ScrollableRegion>
  );
}

export function GradedRepoTable(): ReactElement {
  return (
    <ResultTable
      captionKey="benchmarks.graded.heading"
      headerKeys={[
        'benchmarks.col.framework',
        'benchmarks.col.repo',
        'benchmarks.col.truth',
        'benchmarks.col.recall',
        'benchmarks.col.precision',
      ]}
    >
      {GRADED_REPO_ROWS.map((row) => (
        <tr key={row.repo} className="group border-b border-[color:var(--bd-line)] last:border-b-0">
          <td className={`${TD} font-medium text-[color:var(--bd-ink)]`}>{row.framework}</td>
          <td className={TD}>
            <a
              href={`https://github.com/${row.repo}`}
              target="_blank"
              rel="noreferrer"
              dir="ltr"
              className="underline decoration-[color:var(--bd-dot)] underline-offset-2 transition-colors hover:text-[color:var(--bd-ink)] hover:decoration-[color:var(--bd-muted)]"
            >
              {row.repo}
            </a>
          </td>
          <td className={TD} dir="ltr">
            {row.truthCount}
          </td>
          <td className={TD} dir="ltr">
            {pct(row.recall)}
          </td>
          <td className={TD} dir="ltr">
            {pct(row.precision)}
          </td>
        </tr>
      ))}
    </ResultTable>
  );
}

export function SyntheticSuiteTable(): ReactElement {
  return (
    <ResultTable
      captionKey="benchmarks.synthetic.heading"
      headerKeys={[
        'benchmarks.col.framework',
        'benchmarks.col.fixtures',
        'benchmarks.col.f1',
        'benchmarks.col.threshold',
      ]}
    >
      {SYNTHETIC_ROWS.map((row) => (
        <tr key={row.id} className="group border-b border-[color:var(--bd-line)] last:border-b-0">
          <td className={`${TD} font-medium text-[color:var(--bd-ink)]`} dir="ltr">
            {row.id}
          </td>
          <td className={TD} dir="ltr">
            {row.fixtures}
          </td>
          <td className={TD} dir="ltr">
            {score(row.f1)}
          </td>
          <td className={TD} dir="ltr">
            {score(row.threshold)}
          </td>
        </tr>
      ))}
    </ResultTable>
  );
}

export function VendoredFixtureTable(): ReactElement {
  return (
    <ResultTable
      captionKey="benchmarks.vendored.heading"
      headerKeys={[
        'benchmarks.col.framework',
        'benchmarks.col.fixture',
        'benchmarks.col.structural',
        'benchmarks.col.groundTruth',
      ]}
    >
      {VENDORED_FIXTURE_ROWS.map((row) => (
        <tr key={row.name} className="group border-b border-[color:var(--bd-line)] last:border-b-0">
          <td className={`${TD} font-medium text-[color:var(--bd-ink)]`}>{row.framework}</td>
          <td className={TD} dir="ltr">
            {row.name}
          </td>
          <td className={TD} dir="ltr">
            {score(row.structuralF1)}
          </td>
          <td className={TD} dir="ltr">
            {row.groundTruth}
          </td>
        </tr>
      ))}
    </ResultTable>
  );
}
